import { config } from '@/config';
import { identifyPostHogUser, resetPostHog } from '@/core/analytics';
import { authenticate } from '@/core/auth/authenticate';
import { AppError } from '@/core/helper/app_error';
import { FindFirebaseIdUsecase } from '@/domain/use-cases/users/findFireBaseId';
import {
  ActionCodeSettings,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  getAuth,
  reauthenticateWithCredential,
  getMultiFactorResolver,
  multiFactor,
  MultiFactorResolver,
  sendPasswordResetEmail as sendPasswordResetEmailFirebase,
  signInWithCustomToken,
  signInWithEmailAndPassword as signInWithEmailAndPasswordFirebase,
  signOut,
  TotpMultiFactorGenerator,
  TotpSecret,
  updatePassword,
  User,
} from 'firebase/auth';
import { UsersRepoIml } from '../adapters/repositories';

// Declare global variable for TOTP secret
declare global {
  interface Window {
    _lastGeneratedTotpSecret?: TotpSecret;
  }
}

export interface AuthUser {
  id: string;
  displayName?: string;
  avatarUrl: string;
  email: string;
  emailVerified: boolean;
  loginType: string;
  token: string;
}

const verifyRegistration = async (user: User): Promise<string> => {
  let tokenResult = await user.getIdTokenResult(true);

  if (!tokenResult.claims.id) {
    const token = await user.getIdToken();

    const usersRepoIml = new UsersRepoIml();
    await usersRepoIml.registerWithToken(token);
    do {
      tokenResult = await user.getIdTokenResult(true);
      await new Promise((resolve) => setTimeout(resolve, 1000));
    } while (!tokenResult.claims.id);
  }
  return tokenResult.claims.id;
};

const getUser = async (user: User): Promise<AuthUser> => {
  const id = await verifyRegistration(user);
  const token = await user.getIdToken();

  const usersRepoIml = new UsersRepoIml();
  await usersRepoIml.loginWithToken(token); // response has set-cookie: customToken

  const avatarUrl =
    user.photoURL && user.photoURL.indexOf('facebook') > -1
      ? `${user.photoURL}?height=500`
      : user.photoURL;

  identifyPostHogUser(id);

  const result = {
    id,
    displayName: user.displayName || '',
    avatarUrl: avatarUrl || '',
    email: user.email || '',
    emailVerified: user.emailVerified,
    loginType: 'EMAIL',
    token,
  };

  return result;
};

const generateTotpSecret = async () => {
  // Generate a TOTP secret.
  const auth = getAuth();
  const { currentUser } = auth;
  if (!currentUser) {
    return;
  }
  const multiFactorSession = await multiFactor(currentUser).getSession();
  const totpSecret = await TotpMultiFactorGenerator.generateSecret(multiFactorSession);

  // Store the entire secret object in a global variable for later use
  // We can't store the actual TotpSecret object in localStorage as it loses its methods
  window._lastGeneratedTotpSecret = totpSecret;

  return totpSecret;
};

const verifyAndEnrollTotp = async (verificationCode: string) => {
  const auth = getAuth();
  const { currentUser } = auth;
  if (!currentUser) {
    throw new AppError('auth/user-not-found', 'User not found');
  }

  try {
    // Get the stored TOTP secret from the global variable
    const totpSecret = window._lastGeneratedTotpSecret;
    if (!totpSecret) {
      throw new AppError('auth/totp-secret-not-found', 'TOTP secret not found or expired');
    }

    const multiFactorUser = multiFactor(currentUser);

    // Create a TOTP assertion using the stored secret
    const totpAssertionForEnrollment = TotpMultiFactorGenerator.assertionForEnrollment(
      totpSecret,
      verificationCode,
    );

    // Finalize enrollment
    await multiFactorUser.enroll(totpAssertionForEnrollment, 'TOTP Authenticator App');

    // Clean up stored secret
    delete window._lastGeneratedTotpSecret;

    return true;
  } catch (error) {
    console.error('TOTP enrollment failed:', error);
    throw new AppError('auth/totp-verification-failed', 'Failed to verify TOTP code');
  }
};

const signInWithEmailAndPassword = async (email: string, password: string) => {
  const auth = getAuth();
  const { user } = await signInWithEmailAndPasswordFirebase(auth, email, password);
  if (!user) {
    throw new AppError('auth/user-not-found', 'User not found');
  }

  const localUser = await new FindFirebaseIdUsecase(new UsersRepoIml()).run(user.uid);
  console.log('localUser', localUser);

  // Check if user has MFA enrolled with TOTP
  const idTokenResult = await user.getIdTokenResult(true);
  const mfaInfo = idTokenResult.claims.mfaInfo ?? {};
  const hasTotpEnrolled = mfaInfo.totpEnrolled === true;

  if (!hasTotpEnrolled && localUser?.requiredMfa) {
    throw new AppError('auth/totp-enrollment-required', 'TOTP MFA enrollment required');
  }

  return getUser(user);
};

const signInWithUsernameAndPassword = async (
  username: string,
  password: string,
): Promise<AuthUser> => {
  const usersRepoIml = new UsersRepoIml();
  const customToken = await usersRepoIml.loginWithUsernameAndPassword({
    username,
    password,
  });
  if (!customToken) {
    throw new AppError('auth/user-not-found', 'User not found');
  }

  const auth = getAuth();
  const { user } = await signInWithCustomToken(auth, customToken);
  if (!user) {
    throw new AppError('auth/user-not-found', 'User not found');
  }
  return getUser(user);
};

const signInWithSessionToken = async (sessionToken: string): Promise<AuthUser> => {
  await authenticate(sessionToken);
  const auth = getAuth();
  const { currentUser } = auth;
  if (!currentUser) {
    throw new AppError('auth/user-not-found', 'User not found');
  }
  return getUser(currentUser);
};

const getCurrentUser = async (): Promise<AuthUser | undefined> => {
  try {
    const auth = getAuth();
    const { currentUser } = auth;
    return currentUser ? getUser(currentUser) : undefined;
  } catch {
    return undefined;
  }
};

const logout = async (): Promise<void> => {
  const auth = getAuth();
  await signOut(auth);
  resetPostHog();
};

const changePassword = async (oldPassword: string, newPassword: string): Promise<void> => {
  const auth = getAuth();
  const { currentUser } = auth;
  if (!currentUser?.email) {
    throw new AppError('auth/change-password-failed', 'Change password failed');
  }

  const credential = EmailAuthProvider.credential(currentUser.email, oldPassword);
  await reauthenticateWithCredential(currentUser, credential);
  await updatePassword(currentUser, newPassword);
};

const sendPasswordResetEmail = async (
  email: string,
  _language: string = config.i18n.defaultLang,
): Promise<void> => {
  const auth = getAuth();
  const actionCodeSettings: ActionCodeSettings = {
    url: `${config.base.webUrl}/reset-password`,
    handleCodeInApp: false,
  };
  await sendPasswordResetEmailFirebase(auth, email, actionCodeSettings);
};

const resetPassword = async (
  oobCode: string,
  newPassword: string,
  language: string = config.i18n.defaultLang,
): Promise<void> => {
  const auth = getAuth();
  auth.languageCode = language;
  await confirmPasswordReset(auth, oobCode, newPassword);
};

const mfaResolver = (error: any): MultiFactorResolver => {
  if (error.code !== 'auth/multi-factor-auth-required') {
    throw error;
  }

  const mfaResolver = getMultiFactorResolver(getAuth(), error);

  // Firebase's error object has the resolver property but TypeScript doesn't know about it
  // We need to access it directly from the error object
  return mfaResolver;
};

const verifyMfaCode = async (
  resolver: MultiFactorResolver,
  verificationCode: string,
): Promise<AuthUser> => {
  try {
    // Complete sign-in with TOTP verification
    const totpAssertionForSignIn = TotpMultiFactorGenerator.assertionForSignIn(
      resolver.hints[0].uid,
      verificationCode,
    );

    const userCredential = await resolver.resolveSignIn(totpAssertionForSignIn);
    return getUser(userCredential.user);
  } catch (error) {
    console.error('MFA verification failed:', error);
    throw new AppError('auth/invalid-verification-code', 'Invalid verification code');
  }
};

export const authService = {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithUsernameAndPassword,
  signInWithSessionToken,
  getCurrentUser,
  logout,
  changePassword,
  sendPasswordResetEmail,
  getUser,
  resetPassword,
  generateTotpSecret,
  verifyAndEnrollTotp,
  getMultiFactorResolver,
  verifyMfaCode,
  mfaResolver,
};

export const authErrorCodes = {
  invalidEmail: 'auth/invalid-email',
  userNotFound: 'auth/user-not-found',
  weakPassword: 'auth/weak-password',
  expiredActionCode: 'auth/expired-action-code',
  invalidActionCode: 'auth/invalid-action-code',
  userDisabled: 'auth/user-disabled',
  wrongPassword: 'auth/wrong-password',
  invalidCredential: 'auth/invalid-credential',
};
